import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { TbPdfPreviewService } from './tb-pdf-preview.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
  extractObject,
} from '../../workspace-element-context';

@Component({
    selector: 'gtw-tb-pdf-preview',
    templateUrl: './tb-pdf-preview.component.html',
    styleUrls: ['./tb-pdf-preview.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TbPdfPreviewComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private isInitialized = false;
  private isLoading = false;
  private readonly subs = new Subscription();

  globalService: any;
  GENERAL_CONFIG: any;
  rowData: any = {};
  colData: any = {};
  filterParams: any = {};

  formName: string = '';
  leId: string = '';
  cdrNo: string = '';
  rptPeriod: string | number = 1;
  taxYear: string = '';
  systemFlag: string = 'F';
  scenarioLocal: string = '';
  formSubCat: string = 'SAME_PDF';
  modalTitle: string = '';

  minimise = false;
  loading = false;
  formLoaded = false;
  noDataAvail = false;
  formNotAvail = false;
  formNotAvailText: string = 'Error loading the requested form!';
  pdfContent: SafeResourceUrl | undefined;

  constructor(
    private cdr: ChangeDetectorRef,
    private domSanitizer: DomSanitizer,
    private _snackBar: MatSnackBar,
    private tbPdfPreviewService: TbPdfPreviewService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.isInitialized = false;
        this.isLoading = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.isInitialized = false;
        this.isLoading = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    const gc = header.generalConfigData || {};
    this.GENERAL_CONFIG = {
      ...gc,
      pdf_engine_api_url: gc.pdf_engine_api_url ?? gc.pdfEngine ?? '',
    };
    this.globalService = header;
    this.filterParams = normalizeFilterParams(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0])
      : extractObject(rawRow);
    const d = activeScreen?.data || {};
    const rawCol = d.colData ?? d.col_data ?? d.columnData ?? d.col;
    this.colData = extractObject(rawCol);
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    if (!this.globalService || !this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) {
      return;
    }

    if (!this.colData || Object.keys(this.colData).length === 0) {
      return;
    }

    if (!this.filterParams || Object.keys(this.filterParams).length === 0) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  private init(): void {
    try {
      this.setupPdfParams();
      this.loadPdf();
    } catch (e) {
      console.error('Error in TbPdfPreview init:', e);
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  private setupPdfParams(): void {
    const colMap = this.colData.map;

    if (colMap === 'Pdf_Preview') {
      const taxType = this.rowData.TAX_TYPE;

      if (taxType === 'CFC' || taxType === 'NCFC' || taxType === 'PTSP' || taxType === 'NCPTSP' || taxType === 'NCPTSPNF') {
        this.leId = this.rowData.HO_LEID;
        this.cdrNo = this.rowData.HO_CDR_NO;
        this.rptPeriod = this.rowData.HO_REPORTING_PERIOD || this.rowData.HO_RPT_PD || 1;
        this.taxYear = this.rowData.TAX_YEAR;
        this.formName = (taxType === 'PTSP' || taxType === 'NCPTSP' || taxType === 'NCPTSPNF') ? '8865' : '5471';
      } else if (taxType === 'BRCFC' || taxType === 'BRPTP' || taxType === 'BRUS') {
        this.leId = this.rowData.LEID;
        this.cdrNo = this.rowData.CDR_NO || this.rowData.MARS;
        this.rptPeriod = this.rowData.REPORTING_PERIOD || 1;
        this.taxYear = this.rowData.TAX_YEAR;
        this.formName = '8858';
      } else {
        this.leId = this.rowData.HO_LEID;
        this.cdrNo = this.rowData.HO_CDR_NO;
        this.rptPeriod = this.rowData.HO_REPORTING_PERIOD || this.rowData.HO_RPT_PD || 1;
        this.taxYear = this.rowData.TAX_YEAR;
        this.systemFlag = this.rowData.SOURCE_SYSTEM ? this.rowData.SOURCE_SYSTEM[0] : 'F';
        this.formName = '1120';
      }
    } else if (colMap === '8858_Pdf_Preview') {
      this.leId = this.rowData.LEID_8858;
      this.cdrNo = this.rowData.CDR_NO_8858;
      this.rptPeriod = this.rowData.REPORTING_PERIOD_8858 || this.rowData.RPT_PD_8858 || 1;
      this.taxYear = this.rowData.TAX_YEAR;
      this.formName = '8858';
    }

    this.formSubCat = 'SAME_PDF';
    this.systemFlag = this.systemFlag || 'F';
    this.scenarioLocal = this.filterParams.scenario || this.globalService?.globalParams?.scenario || '';
    this.taxYear = this.taxYear || this.filterParams.tax_year || this.globalService?.globalParams?.tax_year || '';
    this.modalTitle = this.formName;
  }

  private loadPdf(): void {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;
    this.loading = true;
    this.formNotAvail = false;
    this.noDataAvail = false;
    this.cdr.detectChanges();

    const pdfEngineUrl = this.GENERAL_CONFIG.pdf_engine_api_url;

    this.tbPdfPreviewService.doesTaxFormExist(
      pdfEngineUrl,
      this.formName,
      this.taxYear
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (!data || Object.keys(data).length === 0 || (data.fileNotFound && data.fileNotFound.length > 0)) {
          const warningMsg = (!data || Object.keys(data).length === 0)
            ? 'Form ' + this.formName + ' is not found! '
            : data.fileNotFound;
          this._snackBar.open(warningMsg, 'Close', {
            duration: 4000,
            panelClass: ['warning-snackbar']
          });
          this.formNotAvailText = warningMsg;
          this.formNotAvail = true;
          this.loading = false;
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }

        if (data.error && data.error.length > 0) {
          this._snackBar.open(data.error, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.formNotAvailText = data.error;
          this.formNotAvail = true;
          this.loading = false;
          this.isLoading = false;
          this.cdr.detectChanges();
          return;
        }

        const s3Url = data.result;
        const responseFormSubCat = data.form_sub_cat;

        if (this.systemFlag === 'F') {
          this.loadNewProformaPdf(pdfEngineUrl, s3Url);
        } else {
          this.loadLegacyPdf(pdfEngineUrl, s3Url, responseFormSubCat);
        }
      },
      (error: any) => {
        console.error('Error checking tax form existence:', error);
        this.formNotAvail = true;
        this.loading = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  private loadNewProformaPdf(pdfEngineUrl: string, s3Url: string): void {
    this.tbPdfPreviewService.fillNewProformaForm(
      pdfEngineUrl,
      this.formName,
      s3Url,
      this.formSubCat,
      this.leId,
      this.taxYear,
      this.cdrNo,
      this.rptPeriod,
      this.systemFlag,
      this.scenarioLocal,
      '',
      '',
      '',
      '',
      '',
      'NA',
      '',
      'HO',
      '',
      '250',
      'DOM'
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: ArrayBuffer) => {
        this.handlePdfResponse(response);
      },
      (error: any) => {
        console.error('Error loading new proforma PDF:', error);
        this.formNotAvail = true;
        this.loading = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  private loadLegacyPdf(pdfEngineUrl: string, s3Url: string, responseFormSubCat?: string): void {
    this.tbPdfPreviewService.fillFormPorforma(
      pdfEngineUrl,
      this.formName,
      this.leId,
      s3Url,
      responseFormSubCat || this.formSubCat,
      this.taxYear,
      this.cdrNo,
      this.rptPeriod,
      'HO',
      this.systemFlag,
      this.scenarioLocal
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: ArrayBuffer) => {
        this.handlePdfResponse(response);
      },
      (error: any) => {
        console.error('Error loading legacy PDF:', error);
        this.formNotAvail = true;
        this.loading = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );
  }

  private handlePdfResponse(response: ArrayBuffer): void {
    if (!response || response.byteLength === 0) {
      this.noDataAvail = true;
      this.loading = false;
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    const blob = new Blob([response], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    this.pdfContent = this.domSanitizer.bypassSecurityTrustResourceUrl(blobUrl);
    this.formLoaded = true;
    this.loading = false;
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }
}
